import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { Nfc, Loader2, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;

export default function AdminTapToPayPanel() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feePct, setFeePct] = useState("");
  const [feeFlat, setFeeFlat] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/terminal/overview`, auth());
      setData(res.data);
      setFeePct(String(res.data.fee.percent));
      setFeeFlat(String(res.data.fee.flat_cents));
    } catch { /* hidden on failure */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const update = async (body, ok = "Updated") => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/terminal/settings`, body, auth());
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const t = data.totals || {};
  return (
    <Card className="border-border" data-testid="admin-ttp-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Nfc className="w-5 h-5 text-sky-500" /> Tap to Pay (Stripe Terminal)
            </CardTitle>
            <CardDescription>
              Open to every business with Stripe Connect — the switch is a global kill-switch only. Fees are separate from the online payment fee.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Global</span>
            <Switch checked={data.global_enabled} disabled={saving}
              onCheckedChange={(v) => update({ enabled: v }, v ? "Tap to Pay enabled globally" : "Tap to Pay disabled globally")}
              data-testid="admin-ttp-global-switch" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[["Payments", t.paid_count ?? 0], ["Volume", money(t.paid_volume)],
            ["Platform fees", money(t.platform_fees)], ["Stripe fees", t.stripe_fees > 0 ? money(t.stripe_fees) : "—"],
            ["Failures", t.failed_count ?? 0]].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-border px-3 py-2">
              <div className="text-base font-bold text-foreground">{value}</div>
              <div className="text-[10px] text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-border p-3 space-y-2">
          <p className="text-sm font-medium text-foreground">Tap to Pay platform fee</p>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <p className="text-[10px] text-muted-foreground mb-1">Percent (%)</p>
              <Input type="number" step="0.1" className="w-24 h-8" value={feePct} onChange={(e) => setFeePct(e.target.value)} data-testid="admin-ttp-fee-pct" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground mb-1">Flat (cents)</p>
              <Input type="number" className="w-24 h-8" value={feeFlat} onChange={(e) => setFeeFlat(e.target.value)} data-testid="admin-ttp-fee-flat" />
            </div>
            <Button size="sm" disabled={saving}
              onClick={() => update({ fee_percent: parseFloat(feePct), fee_flat_cents: parseInt(feeFlat || "0", 10) }, "Tap to Pay fee updated")}
              data-testid="admin-ttp-fee-save">
              <Save className="w-3.5 h-3.5 mr-1" /> Save fee
            </Button>
            <p className="text-[10px] text-muted-foreground">Applied to NEW payments only. Stripe's own processing fees come from actual balance transactions.</p>
          </div>
        </div>

        <div className="rounded-lg border border-border p-3 space-y-2" data-testid="admin-ttp-businesses">
          <p className="text-sm font-medium text-foreground">Businesses using Tap to Pay ({data.businesses.length})</p>
          <div className="space-y-1">
            {data.businesses.map(b => (
              <div key={b.user_id} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-border/50 last:border-0 flex-wrap" data-testid={`admin-ttp-biz-${b.user_id}`}>
                <div className="min-w-0">
                  <span className="text-foreground font-medium">{b.company_name || b.name || b.email}</span>
                  <span className="text-muted-foreground ml-2">{b.email}</span>
                  <div className="flex gap-1 mt-0.5 flex-wrap">
                    {b.business_enabled && <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[9px]">Enabled</Badge>}
                    {!b.connect_ready && <Badge className="bg-amber-500/15 text-amber-500 border-0 text-[9px]">Stripe not ready</Badge>}
                    {b.location_ready && <Badge className="bg-muted text-muted-foreground border-0 text-[9px]">Location set</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-muted-foreground tabular-nums">{b.paid_count} pmts · {money(b.paid_volume)} · fees {money(b.platform_fees)}{b.failed_count > 0 ? ` · ${b.failed_count} failed` : ""}</span>
                </div>
              </div>
            ))}
            {data.businesses.length === 0 && <p className="text-xs text-muted-foreground">No businesses have turned on Tap to Pay yet.</p>}
          </div>
        </div>

        {data.recent_failures?.length > 0 && (
          <div className="rounded-lg border border-red-500/25 p-3" data-testid="admin-ttp-failures">
            <p className="text-sm font-medium text-foreground mb-1.5">Recent failed / canceled attempts</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {data.recent_failures.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-muted-foreground">{f.invoice_number} · {f.customer_name}</span>
                  <span className="text-red-400">{f.status} · {money(f.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
